import { CompositePropagator, W3CBaggagePropagator, W3CTraceContextPropagator } from '@opentelemetry/core'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions'
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { SpanStatusCode } from '@opentelemetry/api'
import { ApplicationConfiguration } from '../../types'
import { AuthEvent } from 'app/modules/auth/types'
import { monitoringIgnoreUrls } from './monitoring-context'

let provider: WebTracerProvider | null = null

export function initOtel(config?: ApplicationConfiguration['openTelemetry'], version?: string | null): void {
  if (!config) {
    return
  }

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: 'webservice-frontend',
    [ATTR_SERVICE_VERSION]: version ?? config.appVersion,
  })

  const exporter = new OTLPTraceExporter({
    url: config.otlpEndpoint,
  })

  provider = new WebTracerProvider({
    resource,
    spanProcessors: [new BatchSpanProcessor(exporter)],
  })

  provider.register({
    contextManager: new ZoneContextManager(),
    propagator: new CompositePropagator({
      propagators: [new W3CBaggagePropagator(), new W3CTraceContextPropagator()],
    }),
  })

  registerInstrumentations({
    tracerProvider: provider,
    instrumentations: [
      new XMLHttpRequestInstrumentation({
        ignoreUrls: monitoringIgnoreUrls,
        propagateTraceHeaderCorsUrls: [new RegExp(`^${window.location.origin}`)],
      }),
      new FetchInstrumentation({
        ignoreUrls: monitoringIgnoreUrls,
        propagateTraceHeaderCorsUrls: [new RegExp(`^${window.location.origin}`)],
      }),
    ],
  })
}

export function shutdownOtel(): void {
  if (provider) {
    provider.shutdown()
    provider = null
  }
}

/**
 * Record a page view as a point-in-time span.
 * Call this on every route change that should be tracked.
 */
export function traceOtelPageView(routeTemplate: string, url: string, userId?: string): void {
  if (!provider) {
    return
  }

  const span = provider.getTracer('router').startSpan('page_view')
  span.setAttributes({
    'app.route': routeTemplate,
    'http.url': url,
    ...(userId ? { 'enduser.id': userId } : {}),
  })
  span.setStatus({ code: SpanStatusCode.OK })
  span.end()
}

/**
 * Record an auth lifecycle event as a point-in-time span.
 */
export function traceOtelAuthEvent(event: AuthEvent, userId?: string): void {
  if (!provider) {
    return
  }

  const span = provider.getTracer('auth').startSpan(`auth.${event}`)
  if (userId) {
    span.setAttribute('enduser.id', userId)
  }
  span.setStatus({ code: SpanStatusCode.OK })
  span.end()
}
